/**
 * Progress of a saved WiFi rename, derived purely from timestamps the device
 * record already carries — no firmware involvement.
 *
 * Anchors (all set server-side):
 *  - router.ssidRenamedAt — the instant the operator's rename was ACCEPTED.
 *  - router.lastSeenAt    — the router's own last check-in. A beat AFTER the
 *    rename proves the router has been handed the new name.
 *  - lastHeartbeat        — the COIN BOX's liveness. A report well after the
 *    rename proves the box rode through the radio blip onto the new name
 *    (a stranded box goes silent instead — the JOANS failure).
 *
 * Stages, in order:
 *  'router-wait' → saved, router has not checked in since (≤ ~30s normally)
 *  'applying'    → router has it; radio reload + NodeMCU follow in progress
 *  'done'        → confirmed (box reported after the change), shown ~10 min
 *  'stuck'       → the NodeMCU has not come back 3+ min after the rename
 *  null          → nothing in flight (no rename, or it finished a while ago)
 *
 * A vendo with no NodeMCU has no confirmation signal, so 'done' there is
 * time-based (router had it + a minute passed) and the copy stays honest
 * ("should now be broadcasting").
 */

export type RenameStage = 'router-wait' | 'applying' | 'done' | 'stuck' | null;

const APPLY_MARGIN_MS = 60_000; // the router's apply beat after being told
const STUCK_AFTER_MS = 180_000; // box silent this long after a rename = stuck
const NO_BOX_DONE_MS = 90_000; // no-NodeMCU vendos: call it done after this
const SHOW_DONE_FOR_MS = 10 * 60_000; // keep the green state visible a while

export function renameProgressStage(
   device: {
      lastHeartbeat?: string | Date | null;
      nodeMcu?: { mac?: string };
      router?: {
         ssidRenamedAt?: string | Date | null;
         lastSeenAt?: string | Date | null;
      };
   },
   now: number = Date.now()
): RenameStage {
   const at = device.router?.ssidRenamedAt
      ? new Date(device.router.ssidRenamedAt).getTime()
      : 0;
   if (!at) return null;
   const elapsed = now - at;
   if (elapsed < 0 || elapsed > SHOW_DONE_FOR_MS) return null;

   const routerSeen = device.router?.lastSeenAt
      ? new Date(device.router.lastSeenAt).getTime() > at
      : false;
   if (!routerSeen) return 'router-wait';

   const hasCoinBox = !!device.nodeMcu?.mac;
   if (!hasCoinBox) {
      return elapsed >= NO_BOX_DONE_MS ? 'done' : 'applying';
   }

   const boxHeard = device.lastHeartbeat
      ? new Date(device.lastHeartbeat).getTime()
      : 0;
   if (boxHeard > at + APPLY_MARGIN_MS) return 'done';
   if (elapsed >= STUCK_AFTER_MS) return 'stuck';
   return 'applying';
}

/** Plain-English line for each stage (operator-facing — keep it simple). */
export function renameProgressText(
   stage: Exclude<RenameStage, null>,
   hasCoinBox: boolean
): string {
   switch (stage) {
      case 'router-wait':
         return 'Name saved. Waiting for the router to pick it up — under 30 seconds…';
      case 'applying':
         return hasCoinBox
            ? 'The router has the new name and is renaming the WiFi now. The NodeMCU follows it — about a minute…'
            : 'The router has the new name and is renaming the WiFi now — about a minute…';
      case 'done':
         return hasCoinBox
            ? 'Done — the WiFi is broadcasting the new name and the NodeMCU came back online.'
            : 'Done — the WiFi should now be broadcasting the new name.';
      case 'stuck':
         return 'The NodeMCU has not come back since the rename. Check that it has power; if it stays offline, it may be stuck on the old name and need a reflash.';
   }
}
